import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Button, IconButton, Collapse } from "@material-ui/core";
import { Close as CloseIcon, ExpandLess, ExpandMore } from "@material-ui/icons";
import { setShowMemberBanner, setTriggerSignInSignUpModal } from "features/user/userSlice";
import { SIGN_IN_MODAL } from "utils/constants";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    background: "rgba(85, 141, 132, 0.08)",
    borderRadius: theme.shape.borderRadius,
    position: "relative",
    marginBottom: theme.spacing(4),
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  headerText: {
    letterSpacing: "0.19px",
    flex: 1,
  },
  closeButton: {
    padding: theme.spacing(0.5),
    "& .MuiSvgIcon-root": {
      fontSize: "24px",
      color: theme.palette.success.darkText,
    },
  },
  description: {
    marginBottom: theme.spacing(2),
  },
  benefitsList: {
    marginTop: 0,
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    "& li": {
      letterSpacing: "0.16px",
      marginBottom: theme.spacing(0.5),
    },
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    [theme.breakpoints.up(theme.breakpoints.values.tablet)]: {
      flexDirection: "row",
      alignItems: "center",
    },
    paddingLeft: 0,
    gap: theme.spacing(2),
  },

  signInButton: {
    color: theme.palette.success.darkText,
    paddingLeft: 0,
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "underline",
    },
  },
  hideDetailsButton: {
    color: theme.palette.success.darkText,
    marginTop: theme.spacing(1),
    fontSize: "14px",
    justifyContent: "flex-start",
    padding: 0,
    minWidth: "auto",
    maxWidth: "fit-content",
  },
}));

function MemberBanner({ onClose = () => {}, onSignIn }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const showMemberBanner = useSelector((state) => state.user.showMemberBanner);
  const [showDetails, setShowDetails] = useState(true);

  const handleClose = () => {
    dispatch(setShowMemberBanner(false));
    onClose();
  };

  const memberTitle = process.env.REACT_APP_MOLLIES_MEMBER_TITLE;

  const handleToggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const handleJoinNowModal = () => {
    dispatch(setTriggerSignInSignUpModal(SIGN_IN_MODAL.SIGN_UP));
  };

  const handleSignInModal = () => {
    dispatch(setTriggerSignInSignUpModal(SIGN_IN_MODAL.SIGN_IN));
  };

  return showMemberBanner ? (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h6" className={classes.headerText}>
          *Join {memberTitle} for members-only benefits.
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          size="small"
          aria-label="Close banner">
          <CloseIcon />
        </IconButton>
      </div>

      <Collapse in={showDetails}>
        <Typography variant="body2" className={classes.description}>
          Get access to exclusive deals, lower rates, and member-only perks. Sign in or join to
          unlock your available offers.
        </Typography>
        <ul className={classes.benefitsList}>
          <li>Save with our members-only room rates</li>
        </ul>
        <div className={classes.buttonsContainer}>
          <Button
            variant="contained"
            color="secondary"
            disableElevation
            onClick={handleJoinNowModal}>
            Join now
          </Button>
          <Button variant="text" className={classes.signInButton} onClick={handleSignInModal}>
            Already a member? Sign in
          </Button>
        </div>
      </Collapse>

      <Button
        className={classes.hideDetailsButton}
        onClick={handleToggleDetails}
        variant="text"
        endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}>
        {showDetails ? "Hide details" : "More details"}
      </Button>
    </div>
  ) : null;
}

MemberBanner.propTypes = {
  onClose: PropTypes.func,
  onSignIn: PropTypes.func,
};

MemberBanner.defaultProps = {
  onClose: () => {},
  onSignIn: () => {},
};

export default MemberBanner;
